/* screens/settings/quickStart.js — чистая логика бесплатного «Быстрого старта»
   в мастере создания бизнес-пространства (BusinessUpgradeWizard).
   Без React — покрывается vitest напрямую (близнец businessSubtabs.js).

   «Быстрый старт» (biz_demo, 0 ₽ / 7 дней) — один на владельца, и вход в него
   один: этот мастер. Отсюда три правила:
   1. Тому, кто свой период уже использовал, бесплатный тариф не показываем вовсе —
      иначе мастер обещал «7 дней, без карты», а сервер отвечал отказом уже ПОСЛЕ
      создания пространства.
   2. Активация повторяется при сбое: к моменту вызова пространство создано, и без
      периода оно закрыто (Аналитика, касса, ассистент недоступны).
   3. Если период так и не включился, говорим правду и зовём в поддержку: кнопки
      «включить бесплатный период» в приложении нет нигде, кроме этого мастера.
      Пространство без периода закрыто (no_plan) — финал мастера не называет его
      «готовым к работе», а человек попадает на вкладку «Тариф», где это видно, —
      если он всё ещё там, где запустил создание (tariffRedirect): уйти за время
      прогона он мог куда угодно, и выдёргивать его оттуда нельзя.

   ПЕРСОНАЛЬНЫЙ ТАРИФ. Сотруднику администрация может выдать именной закрытый
   бесплатный тариф. В том же ответе /me/ он приходит ключом `personal_plan`
   {plan, duration_days, workspaces_used, workspaces_max} — про зрителя, как и
   `quick_start_available`. Тогда первой карточкой мастера идёт ОН, а не
   «Быстрый старт», и тем же бесплатным путём (POST /trial/) новое пространство
   садится на него: сервер отвечает 'granted'. Отсюда ещё три правила:
   4. Право на карточку решает сервер. Есть ключ — показываем (personalOffer),
      нет — мастер прежний. Какой из бесплатных путей сработает в /trial/, тоже
      решает сервер: статусу верим, какую бы карточку ни показали (ответ /me/ мог
      опоздать к таймеру — тогда на экране «Быстрый старт», а придёт 'granted').
   5. «Быстрый старт» рядом с персональным тарифом не показываем: выбора между
      двумя бесплатными путями у человека нет (wizardTiers).
   6. Срок доступа — `duration_days` выдачи (null — бессрочно) и `expires_at`
      ответа /trial/. `plan.period_days` — окно КВОТ (обычно 30 дней): показать
      его сроком или «/ мес» значило бы пообещать месяц бессрочному тарифу.
   7. Неудача на карточке персонального тарифа называется своими словами
      (trialOutcome / closedPanelText с признаком пути): человек не просил
      «бесплатный период», и «он не включился» — не про него. После СБОЯ тариф
      выбирать не предлагаем — выдача цела, его подключит поддержка; после
      'demo_used' — предлагаем: сервер ответил по ветке «Быстрого старта»,
      значит выдачи у владельца уже нет. */

// Повторы POST /trial/ сверх первой попытки и пауза между ними.
export const TRIAL_RETRIES = 2;
export const TRIAL_RETRY_DELAY_MS = 800;

/* Сколько ждём ответа /api/subscription/me/ про «Быстрый старт». Пока ответа нет,
   линейки тарифов нет и создать пространство нельзя — в том числе платное;
   зависший запрос не должен запирать мастер. Истекло → ведём себя как при упавшем
   запросе (shouldOfferQuickStart(null)): предлагаем, отказ вернёт сам /trial/.
   Тем же ответом приходит и персональный тариф: истекло → карточки его нет
   (personalOffer(null)), но /trial/ включит его и с карточки «Быстрого старта». */
export const QUICK_START_CHECK_TIMEOUT_MS = 6000;

/* Вкладка «Тариф» бизнес-пространства — тот же вход, что у панели «раздел закрыт»
   в Аналитике (PlanLockedPanel) и апселла в ассистенте. Сюда мастер ведёт после
   исхода, оставившего пространство закрытым: строка статуса на карточке текущего
   тарифа говорит, что не так и что делать. */
export const TARIFF_TAB_URL = '/settings?tab=business&biz=tariff';

/* Сколько прогон создания может идти молча. Пока он идёт, мастер не закрыть
   (иначе открыли бы снова и создали второе пространство), а у запросов нет
   таймаута: зависший запрос запирает окно. Дольше этого — мастер объясняет, что
   происходит и что делать. */
export const BUSY_SLOW_HINT_MS = 15000;

/* Подвкладка раздела «Заведения», на которой стоит человек ('tariff', 'team', …),
   либо null — он не в этом разделе. loc — {pathname, search}.
   Без &biz= раздел открывает «Тариф» (SettingsScreen). */
function businessSubtabAt(loc) {
  if (!loc || String(loc.pathname || '').replace(/\/+$/, '') !== '/settings') return null;
  const params = new URLSearchParams(loc.search || '');
  if (params.get('tab') !== 'business') return null;
  return params.get('biz') || 'tariff';
}

/* Вести ли человека на вкладку «Тариф» (TARIFF_TAB_URL) и с какими опциями
   navigate. start — адрес на старте прогона создания, live — адрес сейчас.
   null — не вести.
   Прогон идёт секунды (повторы /trial/; зависший запрос — сколько угодно), и к его
   концу мастер может быть размонтирован, а человек — уйти: в другой раздел или на
   соседнюю подвкладку, куда его как раз зовёт квест онбординга («Команда»).
   Выдёргивать его оттуда нельзя. Ведём, только если он стоит там же, где нажал
   «Создать», — на той же подвкладке раздела «Заведения».
   replace: путь тот же (/settings), меняется только подвкладка — отдельная запись
   истории означала бы «Назад → прежняя подвкладка того же экрана». */
export function tariffRedirect(start, live) {
  const now = businessSubtabAt(live);
  if (!now || now !== businessSubtabAt(start)) return null;
  return { replace: true };
}

/* Предлагать ли «Быстрый старт». subscription — ответ адаптера на
   GET /api/subscription/me/ (ключ quick_start_available).
   Прячем ТОЛЬКО по строгому false: нет ключа (старый бэкенд) или сам запрос
   подписки упал → ведём себя как раньше, отказ в любом случае вернёт /trial/. */
export function shouldOfferQuickStart(subscription) {
  return !(subscription && subscription.quick_start_available === false);
}

/* Персональный тариф зрителя из того же ответа /me/ либо null.
   Отдаём объект как есть: {plan, duration_days, workspaces_used, workspaces_max}
   (адаптер уже привёл `plan` к форме витрины). Ключа нет (старый бэкенд), он
   пуст или запрос подписки упал → null, и мастер ведёт себя как до фичи.
   Без `plan.slug` показывать нечего: карточку рисуют по `plan`, а слаг — ключ
   тарифа в переключателе линейки. */
export function personalOffer(subscription) {
  const offer = subscription && subscription.personal_plan;
  if (!offer || typeof offer !== 'object') return null;
  if (!offer.plan || typeof offer.plan !== 'object' || !offer.plan.slug) return null;
  return offer;
}

/* Линейка тарифов мастера. bases — публичные месячные бизнес-тарифы в порядке
   /plans/ (по цене: бесплатный первым, он же выбор по умолчанию).
   Без «Быстрого старта» бесплатный тариф из линейки уходит, и выбором по
   умолчанию становится первый платный. Бесплатность — по цене из API, тем же
   признаком, каким мастер отличает бесплатный путь от оплаты.
   personal — выдача из personalOffer. С ней первым (и выбором по умолчанию)
   идёт персональный тариф, за ним платные; публичного бесплатного рядом нет,
   доступен он владельцу или нет: обе бесплатные карточки ведут в один и тот же
   /trial/, и что он включит, решает сервер, а не выбор на экране. Тариф с тем же
   слагом из витрины второй раз не встаёт — слаг служит ключом переключателя. */
export function wizardTiers(bases, offerQuickStart, personal = null) {
  const list = bases || [];
  if (personal) {
    return [
      personal.plan,
      ...list.filter((p) => Number(p.price_rub) > 0 && p.slug !== personal.plan.slug),
    ];
  }
  if (offerQuickStart) return list;
  return list.filter((p) => Number(p.price_rub) > 0);
}

/* Подпись тарифа в переключателе линейки. plan — тариф линейки, personal —
   выдача из personalOffer (или null).
   Публичные тарифы — имя без приставки «Business» («S», «M», «L»), как раньше.
   Персональный — постоянное короткое «Персональный», а не его имя: переключатель
   не переносит подписи и прокручивается вбок без полосы, так что длинное имя
   увело бы платные тарифы за край окна на телефоне, а имя вида «Business S»
   после срезания приставки совпало бы с публичным «S». Полное имя — на карточке.
   Имя приходит с сервера: без него подписью служит слаг, а не падение `replace`
   на undefined — оно унесло бы весь мастер вместе с платными тарифами. */
export function tierLabel(plan, personal = null) {
  if (personal && personal.plan && plan.slug === personal.plan.slug) return 'Персональный';
  return String(plan.name || plan.slug).replace(/^Business\s+/i, '');
}

/* Склонение «день». Своё, а не fmt.plural: lib/format.js при загрузке пишет в
   window, а этот модуль обязан импортироваться в node без подготовки (vitest). */
function daysWord(n) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'день';
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return 'дня';
  return 'дней';
}

/* Срок доступа по выдаче персонального тарифа — подпись у карточки.
   durationDays — `duration_days` выдачи: null — «Бессрочно», N — «N дней».
   Только он: `plan.period_days` — окно квот, а не срок (см. шапку файла).
   Значение, которое сроком не прочитать, не называем вовсе (пустая строка):
   «Бессрочно» про выдачу на месяц — обещание, которого никто не давал. */
export function grantTermLabel(durationDays) {
  if (durationDays == null) return 'Бессрочно';
  if (!Number.isInteger(durationDays) || durationDays <= 0) return '';
  return durationDays + ' ' + daysWord(durationDays);
}

/* Сколько пространств уже создано по выдаче: «Пространств: 5 из 15» либо ''.
   Справка, а не замок: достигнут ли лимит, решает сервер — создание ответит
   400 workspace_limit_reached с текстом, и мастер его покажет. */
export function workspacesUsageLabel(offer) {
  const used = offer && offer.workspaces_used;
  const max = offer && offer.workspaces_max;
  if (!Number.isInteger(used) || !Number.isInteger(max) || used < 0 || max <= 0) return '';
  return 'Пространств: ' + used + ' из ' + max;
}

const pause = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/* POST /trial/ с повторами. call — () => Promise<{ok, status, data}> (apiFetch).
   Возвращает {status, expires_at, retried}:
   - status — из ответа ('activated' | 'already' | 'paid_plan' | 'demo_used' |
     'granted') либо null, если ни одна попытка не дала ответа со статусом;
   - expires_at — срок периода из того же ответа (или null; у 'granted' null —
     это бессрочная выдача, а не потерянный срок);
   - retried — ответ пришёл НЕ с первой попытки (до него был хотя бы один сбой).
   Ответ сервера отдаётся как есть: чья это была активация, решает
   resolveTrialStatus ниже.

   Повторяем любой сбой — сеть, не-2xx, 200 без статуса: эндпоинт идемпотентен
   (потерянный ответ удачной активации на повторе вернётся как 'already',
   персонального тарифа — снова как 'granted'), а
   отказы, которые повтор не лечит (403 не-админу, 400 на телефон), в этом пути
   недостижимы — владелец только что созданного пространства админ по построению,
   телефон сюда не передаётся. Ответ со статусом — в том числе 'demo_used' — это
   ОТВЕТ, а не сбой: его не повторяем.
   НЕ бросает: apiFetch не бросает по HTTP-кодам, но сетевой сбой fetch — бросает,
   и исключение оставило бы мастер на форме при уже созданном пространстве
   (повторное «Создать» плодило бы дубли). */
export async function startTrialWithRetry(call, {
  retries = TRIAL_RETRIES, delayMs = TRIAL_RETRY_DELAY_MS, sleep = pause,
} = {}) {
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    if (attempt > 0) await sleep(delayMs);
    let res = null;
    try { res = await call(); } catch { res = null; }
    const data = (res && res.ok && res.data) || null;
    if (data && data.status) {
      return { status: data.status, expires_at: data.expires_at || null, retried: attempt > 0 };
    }
  }
  return { status: null, expires_at: null, retried: true };
}

/* Итоговый статус активации по ответу startTrialWithRetry.
   'already' на ПОВТОРЕ с будущим сроком — это наша же активация: первая попытка
   дошла до сервера и включила период, а ответ потерялся по дороге. Для человека
   это успех («активирован»), а не «уже был активирован» про период, который он
   включил секунду назад.
   Всё остальное остаётся ответом сервера: 'already' с первой попытки (период
   включил кто-то до нас) и 'already' с прошедшим или пустым сроком (период
   использован раньше — рисовать его активным нельзя, см. lib/api/subscription.js).
   'granted' (персональный тариф) проходит как есть с любой попытки: повтор
   отвечает тем же статусом, а пустой срок у него значит «бессрочно». */
export function resolveTrialStatus(result, now = Date.now()) {
  if (!result || !result.status) return null;
  if (result.status === 'already' && result.retried) {
    const expires = result.expires_at ? Date.parse(result.expires_at) : NaN;
    if (expires > now) return 'activated';   // NaN (срок не разобрать) сюда не проходит
  }
  return result.status;
}

// Длинным тостам — больше времени на чтение (дефолт ToastProvider — 4 с).
const LONG_TOAST_MS = 8000;

// closed — пространство после такого исхода ЗАКРЫТО (no_plan): ни периода, ни
// тарифа. По флагу мастер рисует финал без «готово к работе» и ведёт на «Тариф».
const OUTCOMES = {
  activated: { text: 'Бесплатный период активирован', tone: 'success', closed: false },
  already: { text: 'Бесплатный период уже был активирован', tone: 'muted', closed: false },
  paid_plan: { text: 'Тариф уже подключён', tone: 'muted', closed: false },
  // Персональный тариф владельца: пространство посажено на него и открыто.
  // Не «бесплатный период» — срок у выдачи свой, его называет финал мастера.
  granted: { text: 'Тариф подключён', tone: 'success', closed: false },
  // Кап «один на владельца»: не ошибка (пространство создано), но и не успех —
  // без тарифа платные разделы закрыты, поэтому сразу говорим, что делать.
  demo_used: {
    text: 'Бесплатный период уже использован. Пространство создано — '
      + 'выберите тариф, чтобы открыть Аналитику и ассистента',
    tone: 'warning', duration: LONG_TOAST_MS, closed: true,
  },
};

// Период не включился (все попытки упали либо статус незнаком). ТОСТ на вкладку
// «Тариф» не отправляет: включить бесплатный период там нечем, а обещать это
// текстом нельзя. Сам мастер туда ведёт (closed) — показать, что пространство
// закрыто и какой тариф можно выбрать, пока поддержка включает период.
const FAILED = {
  text: 'Пространство создано, но бесплатный период не включился. '
    + 'Напишите в поддержку — включим вручную',
  tone: 'danger', duration: LONG_TOAST_MS, closed: true,
};

/* Те же исходы на карточке ПЕРСОНАЛЬНОГО тарифа, где общий текст был бы
   неправдой (остальные — 'granted', 'activated', 'already', 'paid_plan' — говорят
   о том, что случилось, и от карточки не зависят).
   'demo_used' здесь значит не «период использован», а «выдачи больше нет»:
   действующая выдача всегда отвечает 'granted', а этот статус сервер даёт по
   ветке «Быстрого старта» — тариф отозвали между открытием мастера и кнопкой.
   Человек нажимал «Создать пространство» на персональном тарифе — про него и
   говорим; тариф выбирать предлагаем, потому что выданного уже нет. */
const PERSONAL_OUTCOMES = {
  demo_used: {
    text: 'Персональный тариф недоступен. Пространство создано — '
      + 'выберите тариф, чтобы открыть Аналитику и ассистента',
    tone: 'warning', duration: LONG_TOAST_MS, closed: true,
  },
};

// Тариф не подключился (все попытки упали либо статус незнаком). Не «бесплатный
// период»: его здесь никто не просил. И не «выберите тариф»: выдача цела,
// подключить её может поддержка — совет платить сотруднику был бы неправдой.
const PERSONAL_FAILED = {
  text: 'Пространство создано, но тариф не подключился. '
    + 'Напишите в поддержку — подключим вручную',
  tone: 'danger', duration: LONG_TOAST_MS, closed: true,
};

const has = (map, key) => Object.prototype.hasOwnProperty.call(map, key);

/* Исход активации: {text, tone, duration?} — тост для toast.show();
   closed — осталось ли пространство закрытым (см. выше).
   opts.personal — прогон шёл с карточки персонального тарифа: неудача тогда
   называется его словами (см. PERSONAL_OUTCOMES). Без признака — прежние исходы. */
export function trialOutcome(status, opts) {
  const personal = !!(opts && opts.personal);
  if (personal && has(PERSONAL_OUTCOMES, status)) return PERSONAL_OUTCOMES[status];
  if (has(OUTCOMES, status)) return OUTCOMES[status];
  return personal ? PERSONAL_FAILED : FAILED;
}

/* Строка финала мастера после исхода, оставившего пространство ЗАКРЫТЫМ
   (trialOutcome(...).closed). Под окном в этот момент открыта вкладка «Тариф»:
   строка статуса на карточке тарифа говорит правду для любого пути.
   Совет у путей разный (opts.personal — карточка персонального тарифа):
   - обычный путь — выбрать тариф (прежняя строка);
   - персональный, 'demo_used' — выдачи больше нет: тоже выбрать тариф, но
     сказать, что персональный не подключён;
   - персональный, сбой — тариф подключит поддержка; выбирать (то есть платить)
     не предлагаем, вкладку «Тариф» называем как место, где видно состояние. */
export function closedPanelText(status, opts) {
  const personal = !!(opts && opts.personal);
  if (!personal) {
    return 'Пространство создано. Выберите тариф на вкладке «Тариф», '
      + 'чтобы открыть Аналитику, кассу и ассистента.';
  }
  if (status === 'demo_used') {
    return 'Персональный тариф к пространству не подключён: он больше недоступен. '
      + 'Выберите тариф на вкладке «Тариф», чтобы открыть Аналитику, кассу и ассистента.';
  }
  return 'Пространство создано, но тариф к нему не подключился. '
    + 'Напишите в поддержку — подключим вручную. До этого пространство закрыто: '
    + 'его состояние видно на вкладке «Тариф».';
}

/* Текст финала мастера после 'granted': «Тариф «Имя» подключён бессрочно» /
   «… до <дата>».
   planName — имя тарифа с карточки; пустое, если мастер персональную карточку не
   показывал (ответ /me/ опоздал, а /trial/ всё равно включил тариф) — тогда
   «Персональный тариф».
   expiresAt — срок из ответа /trial/, а не из выдачи: это то, что сервер сделал
   с ЭТИМ пространством; null — бессрочно. Срок, который не разобрать, не
   называем — «до —» хуже молчания.
   formatDate — форматтер даты вызывающего (fmt.date; сюда format.js не тянем,
   см. daysWord). */
export function grantedPanelText(planName, expiresAt, formatDate) {
  const head = planName ? 'Тариф «' + planName + '» подключён' : 'Персональный тариф подключён';
  let term = '';
  if (!expiresAt) term = ' бессрочно';
  else if (!Number.isNaN(Date.parse(expiresAt))) term = ' до ' + formatDate(expiresAt);
  return head + term + '. Пространство готово к работе.';
}
